import * as React from "react";
import { ComboBox, Dropdown, IComboBox, IComboBoxOption, IDropdownOption, ResponsiveMode } from "@fluentui/react";
import { ChoiceProps } from "../../interfaces/props/ChoiceProps";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { logger } from "../../../../utilities/logger";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { appendCustomOptions, resolveChoiceKey, toChoiceList } from "../helpers/choiceOptionsHelper";

const optionKeys = (options: IDropdownOption[]): any[] => options.map(option => option.key);

const getOptions = (allowFreeForm: boolean, defaultOptions: any[], inputValue: string): IDropdownOption[] => {
  // Choices normally arrive deduped from convertMetadataToFields; deduping again here is cheap
  // insurance, because a duplicate key breaks Fluent's first-match selection.
  const choices: any[] = toChoiceList(defaultOptions);
  const options = [{ key: null, text: "" }, ...choices.map(item => ({ key: item, text: item }))];
  // A stored value the field does not define becomes a custom entry, trimmed so it can never
  // seed a whitespace variant; the raw stored value still resolves onto it for display.
  return allowFreeForm && typeof inputValue === "string" ? appendCustomOptions(options, inputValue) : options;
}

export const Choice: React.FunctionComponent<ChoiceProps> = ({
  displayName,
  isRequired,
  inputValue,
  Choices,
  updateInputValidationStatus,
  inputValidator,
  error
}: ChoiceProps) => {
  const languageStrings = React.useContext(LanguageContext);

  const [allowFreeForm] = React.useState<boolean>(inputValue?.xml?.FillInChoice === SharePointMetadataBooleans.true);
  const [options, setOptions] = React.useState<IDropdownOption[]>(() => getOptions(allowFreeForm, Choices, inputValue?.value ? inputValue.value as string : null));
  // The stored value stays raw in inputValue; only the key handed to the control is resolved, so a
  // value that differs from a choice by surrounding whitespace renders as selected without being rewritten.
  const [selectedKey, setSelectedKey] = React.useState<string | number | undefined>(() => resolveChoiceKey(inputValue?.value, optionKeys(options)));

  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      updateInputValidationStatus(inputValidator.validate(inputValue?.value));
    } catch (e) {
      logger(e, "choice.tsx (useEffect)")
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const isFirstValueRun = React.useRef(true);
  React.useEffect(() => {
    if (isFirstValueRun.current) {
      isFirstValueRun.current = false;
      return;
    }
    const value = inputValue?.value;
    // Follow the parent, as the Dropdown branch does: a datasource pick or a file swap on one
    // instance replaces the value from outside, and the control must show what will be saved.
    const nextOptions = allowFreeForm && typeof value === "string" ? appendCustomOptions(options, value) : options;
    if (nextOptions !== options) {
      setOptions(nextOptions);
    }
    setSelectedKey(value === undefined || value === null ? null : resolveChoiceKey(value, optionKeys(nextOptions)));
  }, [inputValue?.value]);

  const onChange = React.useCallback(
    (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
      updateInputValidationStatus(inputValidator.validate(item.key));
    },
    [inputValidator, updateInputValidationStatus]
  );

  const onComboChange = React.useCallback(
    (event: React.FormEvent<IComboBox>, option?: IComboBoxOption, index?: number, value?: string): void => {
      let key = option?.key;
      if (!option && value) {
        // Free-form text: Fluent found no option whose text starts with it (its own matching is
        // case-insensitive but never trimmed). Resolve it exactly like a stored value, so a
        // whitespace or case variant of an existing option selects that option. Only genuinely
        // new text becomes a custom entry, trimmed so it can never seed a whitespace variant of its own.
        const keys = optionKeys(options);
        const resolved = resolveChoiceKey(value, keys);
        if (keys.includes(resolved)) {
          key = resolved;
        }
        else {
          const custom = value.trim();
          if (!custom) return;
          key = custom;
          setOptions(prevOptions => [...prevOptions, { key: custom, text: custom }]);
        }
      }
      setSelectedKey(key);
      updateInputValidationStatus(inputValidator.validate(key));
    },
    [inputValidator, options, updateInputValidationStatus]
  );

  return allowFreeForm
    ?
    <ComboBox
      allowFreeform
      useComboBoxAsMenuWidth
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={selectedKey}
      label={displayName}
      options={options}
      onChange={onComboChange}
      required={isRequired()}
      errorMessage={error}
    />
    :
    <Dropdown
      label={displayName}
      disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
      selectedKey={resolveChoiceKey(inputValue?.value, optionKeys(options))}
      onChange={onChange}
      placeholder={languageStrings.SelectOption}
      options={options}
      required={isRequired()}
      errorMessage={error}
      className="xb-sp-input"
      // responsiveMode={ResponsiveMode.small}
    />;
};

export default Choice;
